import React, { useEffect } from 'react';
import {
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button, message
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  CustomersFilter,
  CustomersFilterOperator,
  CustomersFilterOperatorType,
  customersFilterPropertiesOptions,
  CustomersFilterProperty
} from '@next-is-free-monorepo/next-is-free-types';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { apiV1 } from '../../utils/apiClient';
import { IconFilter, IconPlus } from '@tabler/icons-react';
import CModal from '../CModal';
import { salesPointsQueryOptions } from '../../utils/queryOptions';
import { NActionIcon, NButton, NModal } from '@next-is-free-monorepo/nif-web-components';
import { CancelIcon, FilterIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';

const operatorOptions: Record<CustomersFilterOperatorType, {
  label: string;
  value: CustomersFilterOperator;
}[]> = {
  number: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' },
    { label: '> Maggiore di', value: 'more_than' },
    { label: '< Minore di', value: 'less_than' }
  ],
  date: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' },
    { label: 'Dopo di', value: 'after' },
    { label: 'Prima di', value: 'before' }
  ],
  enum: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' }
  ],
  string: [
    { label: '= Uguale a', value: 'equal' },
    { label: '≠ Diverso da', value: 'not_equal' }
  ]
};

interface CustomersFiltersModalProps {
  open: boolean;
  onClose: () => void;
  rules: CustomersFilter[];
  onFinish: (rules: CustomersFilter[]) => void
}

const CustomerFiltersForm = ({ open, onClose, rules, onFinish }: CustomersFiltersModalProps) => {
  const [form] = Form.useForm();

  const initialValues = rules.map(rule => {
    if(rule.operator_type === 'date'){
      return {...rule, value: dayjs(rule.value)}
    }
    if(rule.property === 'metadata'){
      return {...rule, property: 'metadata_'+rule.metadata_key}
    }
    return rule
  })

  const metadata = useSuspenseQuery({
    queryKey: ['customers-metadata'],
    queryFn: async () => await apiV1.get<{ key: string }[]>('/shop/customers/metadata'),
    staleTime: Infinity
  });

  const propertiesOptions = [
    {
      label: 'Proprietà',
      title: 'Proprietà',
      options: customersFilterPropertiesOptions.map((p) => ({ label: p.label, value: p.value }))
    },
    {
      label: 'Campi Personalizzati',
      title: 'metadata',
      options: metadata.data.map((item) => ({ label: item.key, value: 'metadata_' + item.key }))
    }
  ];

  const handleFinish = (values: {filters: CustomersFilter[]}) => {

    //clean metadata property
    const validRules: CustomersFilter[] = values.filters.map((item)=>{
      if(item.property.startsWith('metadata_') && item.operator_type === 'string'){
        return({...item, property: 'metadata'})
      }else{
        return item
      }
    })

    onFinish(validRules)
  };

  return (
    <NModal title={'Modifica i filtri clienti'} width={700} customFooter={<div/>}
            open={open} onClose={onClose} hugeIcon={FilterIcon}>
      <div className={'mt-5'}>
        <Form form={form} onFinish={handleFinish} layout="vertical" initialValues={{ filters: initialValues }}>
          <Form.List name="filters">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name }) => (
                  <FilterRowFormItem
                    key={key}
                    name={name}
                    fieldKey={key}
                    remove={remove}
                    form={form}
                    propertiesOptions={propertiesOptions}
                  />
                ))}
                <div className={'flex justify-between mt-6'}>
                  <NButton onClick={() => add()} color={'link'} size={'small'} hugeIcon={PlusSignIcon}>Aggiungi regola</NButton>
                  <div className={'flex gap-2'}>
                    <NButton color={'tertiary'} size={'small'} onClick={onClose}>Annulla</NButton>
                    <Form.Item>
                      <NButton type={'submit'} size={'small'}>Salva</NButton>
                    </Form.Item>
                  </div>
                </div>
              </>
            )}
          </Form.List>
        </Form>
      </div>
    </NModal>
  );
};

const FilterRowFormItem = ({
                             name,
                             fieldKey,
                             remove,
                             form,
                             propertiesOptions
                           }: {
  name: number;
  fieldKey: React.Key;
  remove: (name: number) => void;
  form: any;
  propertiesOptions: { label: string; title: string; options: { label: string; value: string }[] }[];
}) => {
  const selectedProperty = Form.useWatch(['filters', name, 'property'], form);

  function getOperatorType(property: string) {
    return customersFilterPropertiesOptions.find(p => p.value === property)?.type
      || (selectedProperty?.startsWith?.('metadata_') ? 'string' : 'string');
  }

  const selectedType = getOperatorType(selectedProperty);


  function onPropertyChange(value: string) {

    let metadataKey = undefined
    if(value.startsWith('metadata_')){
      metadataKey = value.split('metadata_')[1]
    }else{
      metadataKey = undefined
    }

    form.setFields([
      { name: ['filters', name, 'property'], value },
      { name: ['filters', name, 'operator_type'], value: getOperatorType(value) },
      { name: ['filters', name, 'metadata_key'], value: metadataKey },
      { name: ['filters', name, 'operator'], value: 'equal' },
      { name: ['filters', name, 'value'], value: undefined }
    ]);
  }

  const salesPoints = useSuspenseQuery(salesPointsQueryOptions());
  let salesPointsOptions = salesPoints.data.map((point) => ({
    value: point.id,
    label: point.name
  }));



  return (
    <div key={fieldKey} className="flex gap-2 items-center mb-3">
      <div className={'flex items-center gap-x-2 w-full flex-wrap'}>
        <Form.Item
          name={[name, 'property']}
          rules={[{ required: true, message: 'Seleziona una proprietà' }]}
          className="min-w-[200px] flex-grow-[2] max-h-[22px]"
        >
          <Select onChange={onPropertyChange} placeholder="Proprietà" options={propertiesOptions} />
        </Form.Item>
        <Form.Item hidden name={[name, 'operator_type']}>
          <Input/>
        </Form.Item>
        <Form.Item hidden name={[name, 'metadata_key']}>
          <Input/>
        </Form.Item>
        <Form.Item
          name={[name, 'operator']}
          rules={[{ required: true, message: 'Seleziona un operatore' }]}
          className="min-w-[150px] flex-1 max-h-[22px]"
        >
          <Select placeholder="Operatore" options={operatorOptions[selectedType]} />
        </Form.Item>
        <Form.Item
          name={[name, 'value']}
          rules={[{ required: true, message: 'Inserisci un valore' }]}
          className="min-w-[150px] flex-1 max-h-[22px]"
        >
          {selectedType === 'number' && <InputNumber className={'w-full'} />}
          {selectedType === 'string' && <Input className={'w-full'} />}
          {selectedType === 'date' && <DatePicker className={'w-full'} format={'DD/MM/YYYY'} />}
          {selectedType === 'enum' && (
            <Select options={salesPointsOptions} />
          )}
        </Form.Item>
      </div>
      <NActionIcon hugeIcon={CancelIcon} size={'xs'} color={'dangerTransparent'} onClick={() => remove(name)}/>
    </div>
  );
};

export default CustomerFiltersForm;
